"use client";
import React, { useEffect, useState } from "react";
import "../app/custom-pricing/custom-pricing.css";
import { Col, Container, Form, Row } from "react-bootstrap";
import Image from "next/image";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { LiaIndustrySolid } from "react-icons/lia";
import { FaProjectDiagram } from "react-icons/fa";
import { TbTopologyComplex } from "react-icons/tb";
import { MdOutlineWorkOutline } from "react-icons/md";
import { GiPlatform } from "react-icons/gi";
import { MdOutlineDesignServices } from "react-icons/md";
import { AiOutlineNotification } from "react-icons/ai";

// Image Imports
import WebApp from "../../public/assets/images/custom-pricing/Web-App.png";
import MobileApp from "../../public/assets/images/custom-pricing/Mobile-App.png";
import Both from "../../public/assets/images/custom-pricing/Both.png";
import BackendOnly from "../../public/assets/images/custom-pricing/Backend-only.png";
import CommonButton from "@/components/CommonButton";
import Link from "next/link";
import ProtectedRoute from "@/components/ProtectedRoute";
import { createLead, getQuestionOption } from "@/services/pricing";
import { useRouter } from "next/navigation";
import { showToast } from "../../utils/libs/toast-utilis";
import { Loading } from "@/components/Loading";
import { PROJECT_ESTIMATOR } from "../../utils/libs/utils";
import { event } from "@/lib/analytics";

const techStacks = [
  [
    "angular_logo_240d8fd911.svg",
    "c_logo_ec9cd5acb4.svg",
    "express_js_logo_1ad443d385.svg",
    "firebase_logo_b9c786dd7d.svg",
    "flutter_logo_f74e3cbc2d.svg",
  ],
  [
    "html5_logo_1176002236.svg",
    "iot_logo_0312041018.svg",
    "java_vertical_logo_8957434553.svg",
    "javascript_logo_f4b1c569e7.svg",
    "kotlin_logo_609a713d60.svg",
  ],
  [
    "laravel_logo_3564e2fbbc.svg",
    "mongodb_logo_83f2d5a1bc.svg",
    "mysql_logo_06b69f9930.svg",
    "nodejs_logo_4703833cf2.svg",
    "php_logo_60399407da.svg",
  ],
  [
    "python_logo_bd240427e1.svg",
    "react_logo_6178518320.svg",
    "ruby_logo_fa52d2c016.svg",
    "typescript_wordmark_logo_5c10572ef1.svg",
    "vuejs_logo_9927255915.svg",
  ],
].map((group) => group.map((name) => require(`../../public/assets/images/custom-pricing/${name}`)));

const industries = [
  "🏥 Healthcare",
  "🚚 Logistics",
  "💰 Finance",
  "🛍️ Retail",
  "🌾 Agriculture",
  "👩‍🏫 Education",
  "🛠️ Construction",
  "🫱🏻‍🫲🏼 Business Services",
  "🏭 Manufacturing",
  "✈️ Travel",
  "🎨 Arts",
  "🏠 Real Estate",
  "🌐 Other",
];
const stapesOption = {
  10: "< 1 Month",
  20: "< 2 Month",
  30: "< 3 Month",
  40: "< 4 Month",
  50: "< 5 Month",
  60: "< 6 Month",
  100: "> 6 Month",
};
const stapesResponsiveOption = [
  "< 1 Month",
  "< 2 Month",
  "< 3 Month",
  "< 4 Month",
  "< 5 Month",
  "< 6 Month",
  "> 6 Month",
];
const engagementOptions = [
  { title: "Dedicated", description: "we handle everything" },
  { title: "Contract", description: "client has some in-house team" },
];

const platformOptions = [
  { title: "Web App", image: WebApp },
  { title: "Mobile App", image: MobileApp },
  { title: "Both", image: Both },
  { title: "Backend-only", image: BackendOnly },
];

// Reusable UI Components
const OptionBox = ({ label, selected, onSelect }) => (
  <div className={`option-box ${selected ? "active" : ""}`} onClick={onSelect}>
    {label}
  </div>
);

const ImageOptionBox = ({ label, image, selected, onSelect }) => (
  <div
    className={`option-img-box ${selected ? "active" : ""}`}
    onClick={onSelect}
  >
    <Image
      src={image}
      alt={label}
      width={35}
      height={35}
      className="img-fluid"
    />
    <span>{label}</span>
  </div>
);

const RadioOptionBox = ({ label, selected, onSelect, details }) => (
  <div
    className={`option-radio-box ${selected ? "active" : ""}`}
    onClick={onSelect}
  >
    <div className="radio-ui" />
    <div className="text-center">
      <span>{label}</span>
      {details && <p>{details}</p>}
    </div>
  </div>
);

const SummaryItem = ({ title, value, active, icon }) => (
  <div className="d-flex gap-3 position-relative" style={{ zIndex: "10" }}>
    <div className={`step-box ${active ? "active" : ""}`}>{icon}</div>
    <div className="summary-item">
      <h3>{title}</h3>
      {value ? <span>{value}</span> : <span>Select Option</span>}
    </div>
  </div>
);

const CustomPricingPage = () => {
  const [questionOptions, setQuestionOption] = useState(null);
  const [selectedValues, setSelectedValues] = useState({
    marketingExpertise: false,
    designTeam: false,
    // platform,
    // engagement,
    complexity: "medium",
    duration: "< 6 Month",
    // industries,
  });
  const [loading, setLoading] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const router = useRouter();
  const fetchApi = async () => {
    try {
      const platform = await getQuestionOption("target-platform");
      const industries = await getQuestionOption("industry");
      const engagement = await getQuestionOption("engagement-type");
      setQuestionOption({
        platform,
        industries,
        engagement,
      });
      const engagementValue = engagement?.find(
        (item) => item?.title === "Dedicated"
      )?._id;
      const targetPlatformValue = platform?.find(
        (item) => item?.title === "Both"
      )?._id;
      setSelectedValues({
        ...selectedValues,
        engagement: engagementValue,
        platform: targetPlatformValue,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchApi();
  }, []);
  const renderImageColumns = (group) => (
    <div className="d-flex flex-column gap-3">
      {group.map((img, idx) => (
        <div className="img-container" key={idx}>
          <Image src={img} alt={`tech-${idx}`} className="img-fluid" />
        </div>
      ))}
    </div>
  );
  const handleClearAll = () => {
    const getCustomerDetails = localStorage?.getItem("authDetails");
    const customerDetailsPares = getCustomerDetails
      ? JSON.parse(getCustomerDetails)
      : {};
    event({
      action: "clear lead details",
      params: {
        page: "custom_pricing_page",
        ...selectedValues,
        CustomerId: customerDetailsPares?._id,
      },
    });
    setSelectedValues(null);
  };

  const handelChange = (key, value) => {
    setSelectedValues((pre) => ({ ...pre, [key]: value }));
  };

  const handleViewPlan = async () => {
    setLoadingBtn(true);
    const getCustomerDetails = localStorage?.getItem("authDetails");
    const customerDetailsPares = getCustomerDetails
      ? JSON.parse(getCustomerDetails)
      : {};
    try {
      const res = await createLead({
        ...selectedValues,
        CustomerId: customerDetailsPares?._id,
      });
      if (res?._id?.length > 0) {
        event({
          action: "create lead",
          params: {
            page: "custom_pricing_page",
            ...res,
          },
        });
        router.replace(`/project-price?quoteId=${res?._id}`);
      }
      setLoadingBtn(true);
    } catch (error) {
      showToast.error("Something went wrong. Please try again.");
      event({
        action: "create lead error",
        params: {
          page: "custom_pricing_page",
          ...selectedValues,
          CustomerId: customerDetailsPares?._id,
        },
      });
      setLoadingBtn(false);
    }
  };
  if (loading) {
    return <Loading />;
  }
  return (
    <ProtectedRoute>
      <div>
        {/* Hero Section */}

        <div className="price-header-hero">
          <Container>
            <div className="d-flex gap-3 hero-abs-section">
              {renderImageColumns(techStacks[0])}
              <div className="mt-5">{renderImageColumns(techStacks[1])}</div>
            </div>
            <div
              className="d-flex gap-3 hero-abs-section"
              style={{ right: 0, left: "unset" }}
            >
              <div className="mt-5">{renderImageColumns(techStacks[2])}</div>
              {renderImageColumns(techStacks[3])}
            </div>
            <Row className="justify-content-center">
              <Col xl={8} md={12} className="text-center">
                <h1 className="com-title">
                  Project <span>Estimator</span>
                </h1>
                <p>
                  The project estimator is your direct path to pricing your
                  software solution. Answer 5 key questions crafted by our
                  experts.
                </p>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Form Section */}
        <div className="pricing-container">
          <Container>
            <Row>
              <Col lg={8}>
                <Row className="gy-4">
                  {PROJECT_ESTIMATOR?.map((question, index) => {
                    const html = question?.question.replace(
                      /\*\*(.*?)\*\*/g,
                      '<span className="highlight">$1</span>'
                    );
                    return (
                      <Col md={question?.column} className="mb-5" key={index}>
                        <div className="title-container">
                          <h2
                            className="com-title"
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                          {question?.description?.length > 0 && (
                            <p>{question?.description}</p>
                          )}
                        </div>
                        {question?.type === "chip" ? (
                          <div className="d-flex flex-wrap gap-3">
                            {(typeof question?.option === "string"
                              ? questionOptions?.[question?.option]
                              : question?.option
                            )?.map((item, idx) => (
                              <OptionBox
                                key={idx}
                                label={item?.title}
                                selected={
                                  item?._id === selectedValues?.[question?.key]
                                }
                                onSelect={() =>
                                  handelChange(question?.key, item?._id)
                                }
                              />
                            ))}
                          </div>
                        ) : question?.type === "slider" ? (
                          <>
                            <div className="res-slider-none">
                              <Slider
                                defaultValue={
                                  selectedValues?.[question?.key] ?? 0
                                }
                                dotStyle={{
                                  background: "#ff9900",
                                  height: "15px",
                                  width: "15px",
                                  borderColor: "#fff",
                                  boxShadow:
                                    "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                                }}
                                activeDotStyle={{
                                  background: "#ff9900",
                                  height: "15px",
                                  width: "15px",
                                  borderColor: "#fff",
                                  boxShadow:
                                    "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                                }}
                                marks={question?.option}
                                step={null}
                                // value={selectedValues?.[question?.key] ?? 0}
                                onChange={(event) => {
                                  handelChange(
                                    question?.key,
                                    question?.option[event]
                                  );
                                }}
                              />
                            </div>
                            <div className="res-select-box">
                              <div className="d-flex flex-wrap gap-3">
                                {Object.entries(question?.option || {}).map(
                                  ([value, label]) => (
                                    <OptionBox
                                      key={value}
                                      label={label}
                                      selected={
                                        selectedValues?.[question?.key] ===
                                        label
                                      }
                                      onSelect={() =>
                                        handelChange(question?.key, label)
                                      }
                                    />
                                  )
                                )}
                              </div>
                            </div>
                          </>
                        ) : question?.type === "radio" ? (
                          <div className="d-flex gap-3 custom-width">
                            {(typeof question?.option === "string"
                              ? questionOptions?.[question?.option]
                              : question?.option
                            )?.map((item, index) => (
                              <RadioOptionBox
                                key={index}
                                label={item?.title}
                                selected={
                                  item?._id === selectedValues?.[question?.key]
                                }
                                onSelect={() =>
                                  handelChange(question?.key, item?._id)
                                }
                              />
                            ))}
                          </div>
                        ) : question?.type === "radio-description" ? (
                          <div className="d-flex gap-3">
                            {(typeof question?.option === "string"
                              ? questionOptions?.[question?.option]
                              : question?.option
                            )?.map((item, index) => (
                              <RadioOptionBox
                                key={index}
                                label={item?.title}
                                selected={
                                  item?._id === selectedValues?.[question?.key]
                                }
                                onSelect={() =>
                                  handelChange(question?.key, item?._id)
                                }
                                details={item?.short_description}
                              />
                            ))}
                          </div>
                        ) : null}
                      </Col>
                    );
                  })}
                </Row>
              </Col>

              {/* Summary Section */}
              <Col lg={4} className="ms-auto">
                <div className="summary-box-main">
                  <div className="summary-box">
                    {PROJECT_ESTIMATOR?.map((item, index) => (
                      <SummaryItem
                        key={index}
                        title={item?.summaryTitle}
                        value={
                          item?.key === "duration"
                            ? selectedValues?.[item?.key]
                            : (typeof item?.option === "string"
                                ? questionOptions?.[item?.option]
                                : item?.option
                              )?.find(
                                (option) =>
                                  option?._id === selectedValues?.[item?.key]
                              )?.title
                        }
                        active={!!selectedValues?.[item?.key]?.toString()}
                        icon={item?.summaryIcon}
                      />
                    ))}
                    {/* <SummaryItem
                      title="Industry"
                      value={selectedIndustry}
                      active={!!selectedIndustry}
                      icon={<LiaIndustrySolid />}
                    />
                    <SummaryItem
                      title="Project Length"
                      value={projectLength}
                      active={!!projectLength}
                      icon={<FaProjectDiagram />}
                    />
                    <SummaryItem
                      title="Complexity"
                      value={complexity}
                      active={!!complexity}
                      icon={<TbTopologyComplex />}
                    />
                    <SummaryItem
                      title="Engagement"
                      value={engagement}
                      active={!!engagement}
                      icon={<MdOutlineWorkOutline />}
                    />
                    <SummaryItem
                      title="Platform"
                      value={platform}
                      active={!!platform}
                      icon={<GiPlatform />}
                    />
                    <SummaryItem
                      title="Design Team"
                      value={needDesign}
                      active={!!needDesign}
                      icon={<MdOutlineDesignServices />}
                    />
                    <SummaryItem
                      title="Marketing"
                      value={needMarketing}
                      active={!!needMarketing}
                      icon={<AiOutlineNotification />}
                    /> */}
                  </div>
                  <div className="d-flex gap-3 mt-3">
                    <CommonButton
                      label="Clear All"
                      className="black-border w-50"
                      variant="outline"
                      onClick={handleClearAll}
                    />
                    {/* <Link href="/project-price" className="w-50"> */}
                    <CommonButton
                      label="View Price"
                      className="btn-primary w-100"
                      onClick={handleViewPlan}
                      disabled={
                        !PROJECT_ESTIMATOR?.every(
                          (item) => !!selectedValues?.[item?.key]?.toString()
                        ) || loadingBtn
                      }
                    />
                    {/* </Link> */}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default CustomPricingPage;
